/**
 * NotificationItem Component
 *
 * Renders a single notification with type-specific icon, message, and actions.
 */

import { useCountdown } from '../../hooks/useInviteNotifications';
import { formatRelativeTime, type UnifiedNotification } from '../../hooks/useNotifications';
import type { InviteNotification } from '@drawn-of-war/shared';
import styles from './NotificationDropdown.module.css';

interface NotificationItemProps {
  notification: UnifiedNotification;
  onAccept?: () => void;
  onDecline?: () => void;
  onView?: () => void;
  onMarkAsRead?: () => void;
  isLoading?: boolean;
}

/**
 * Get icon for notification type
 */
function NotificationIcon({ type, subtype }: { type: string; subtype: string }) {
  if (type === 'invite') {
    return subtype === 'challenge' ? (
      // Crossed swords for challenge
      <span className={styles.iconEmoji}>&#9876;&#65039;</span>
    ) : (
      // Mail for lobby invite
      <span className={styles.iconEmoji}>&#128232;</span>
    );
  }

  if (type === 'trade') {
    // Handshake/swap for trade
    return <span className={styles.iconEmoji}>&#129309;</span>;
  }

  // Default bell
  return <span className={styles.iconEmoji}>&#128276;</span>;
}

export function NotificationItem({
  notification,
  onAccept,
  onDecline,
  onView,
  onMarkAsRead,
  isLoading = false
}: NotificationItemProps) {
  const timeLeft = notification.expiresAt ? useCountdown(notification.expiresAt) : null;
  const isExpired = timeLeft === 'Expired';

  const handleClick = () => {
    if (onMarkAsRead && !notification.isRead) {
      onMarkAsRead();
    }
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAccept?.();
  };

  const handleDecline = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDecline?.();
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView?.();
  };

  return (
    <div
      className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''}`}
      onClick={handleClick}
      role="menuitem"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <div className={styles.itemIcon}>
        <NotificationIcon type={notification.type} subtype={notification.subtype} />
      </div>

      <div className={styles.itemContent}>
        <div className={styles.itemHeader}>
          <span className={styles.itemTitle}>{notification.title}</span>
          {notification.isYourTurn && (
            <span className={styles.turnBadge}>Your turn</span>
          )}
        </div>
        <p className={styles.itemMessage}>{notification.message}</p>
        {/* Show password for closed challenge invites */}
        {notification.type === 'invite' && (() => {
          const inviteData = notification.data as InviteNotification;
          return inviteData.isPublic === false && inviteData.password ? (
            <div className={styles.passwordInfo}>
              🔒 Password: <code className={styles.passwordCode}>{inviteData.password}</code>
            </div>
          ) : null;
        })()}
        <div className={styles.itemMeta}>
          <span className={styles.itemTime}>
            {formatRelativeTime(notification.timestamp)}
          </span>
          {timeLeft && !isExpired && (
            <span className={styles.itemExpiry}>
              Expires in {timeLeft}
            </span>
          )}
          {isExpired && (
            <span className={styles.itemExpired}>Expired</span>
          )}
        </div>
      </div>

      {notification.actions.length > 0 && !isExpired && (
        <div className={styles.itemActions}>
          {notification.actions.map((action) => (
            <button
              key={action.type}
              className={`${styles.actionBtn} ${styles[action.variant]}`}
              onClick={action.type === 'accept' ? handleAccept :
                       action.type === 'decline' ? handleDecline :
                       handleView}
              disabled={isLoading}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {!notification.isRead && (
        <div className={styles.unreadDot} aria-label="Unread" />
      )}
    </div>
  );
}
